import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import {
  AlertTriangle,
  ArrowLeft,
  CheckCircle2,
  Cpu,
  CircleSlash,
  FileText,
  GitCompare,
  PauseCircle,
  Pencil,
  Plus,
  Save,
  ShieldCheck,
  Upload,
} from 'lucide-react'
import MessengerLayout from '../../layout/MessengerLayout'
import '../../components/cases/cases.css'
import {
  Button,
  Chip,
  Field,
  Modal,
  Notice,
  Panel,
  Refusal,
  Spinner,
} from '../../components/cases/ui'
import { OrderedList, slugFromLabel } from '../../components/admin/cases/StageEditor'
import PublishEffects, { BlockingFindings }
  from '../../components/admin/workflow/PublishEffects'
import {
  chainTransitions,
  findingsFor,
  isLinearChain,
  resequence,
  versionFindings,
} from '../../components/admin/workflow/graph'
import * as api from '../../components/admin/workflow/api'

// ======================================================================
// THE BUILDER — /admin/workflow-engine/:key
// ======================================================================
//
// THE ONE THING THIS SCREEN MUST NOT DO IS IMPLY THAT EDITING THE GRAPH
// CHANGES BEHAVIOUR. 56 of the 57 catalogue definitions are
// execution_owner='code': bespoke code that already ships drives them,
// and it will keep driving them whether or not anyone presses Publish,
// and it will keep driving them if the definition is edited to say
// something else entirely. This is the screen where somebody is most
// likely to believe otherwise, because it has a step list and a Publish
// button and every other builder they have ever used rewired something.
//
// So the A1 effects table is not tucked into a detail panel here. It is
// the first thing under the header, permanently expanded, sent by the
// backend (wf_engine/routes.py:_publish_effect) so it cannot drift from
// what publish actually does, and repeated in the confirmation dialog.
//
// ----------------------------------------------------------------------
// THE STEP LIST IS StageEditor's OrderedList, NOT A SECOND EDITOR.
// DECISIONS01 §9 answer 5 asked for exactly that. What is genuinely
// shared is not markup but a discipline: both lists edit an array whose
// ORDER IS THE CONFIGURATION and whose items carry a key something else
// has already stored — er_cases.current_stage for one,
// workflow_step_executions.step_key and workflow_instances
// .current_step_keys for the other. The frozen-key rule at the top of
// StageEditor.jsx is the same rule for the same reason.
//
// ----------------------------------------------------------------------
// VALIDATION IS INLINE AND ANCHORED, NEVER A TOAST. Every Finding the
// server returns carries step_key where the rule is about a step (that
// is why the field exists — see wf_engine/validation.py). A message that
// appears in a corner and fades has to be memorised before it can be
// acted on; one drawn on the row names the field to change.
//
// ----------------------------------------------------------------------
// NO VOCABULARY IS HARDCODED HERE. Step types, actors, approver
// strategies, notification types, on-timeout actions and review states
// all come from GET /meta, which reads them from wf_engine/models.py and
// wf_engine/registries.py — the same tuples the CHECK constraints
// mirror. AdminModulesPage.jsx records in its own header that it is the
// second of four hardcoded copies of the module catalogue with nothing
// reconciling them; enumerating STEP_TYPES here would be that mistake
// against a list the database enforces.
// ======================================================================

const OWNER = {
  code: {
    icon: FileText,
    label: 'Documented',
    tone: 'blue',
    help:
      'Bespoke code drives this workflow. The definition documents what that ' +
      'code does and is the schema its step events are reported against. The ' +
      'engine will never run it.',
  },
  engine: {
    icon: Cpu,
    label: 'Engine-run',
    tone: 'accent',
    help: 'The engine executes this definition. The graph is an instruction.',
  },
  none: {
    icon: CircleSlash,
    label: 'Not implemented',
    tone: undefined,
    help: 'Documented, but nothing drives it yet.',
  },
}

// WHERE "BACK" GOES. The library's canonical home is the Admin Settings
// tab, not the standalone /admin/workflow-engine route — that route stays
// mounted so existing deep links resolve, but it is no longer where
// anyone arrives from. The ?tab= is why AdminDocumentsPage keeps its tab
// in the URL: without it, leaving the builder would drop somebody on the
// Organisation tab, which is the same "where did my screen go" problem
// this move exists to fix, one level down.
const LIBRARY = '/admin/documents?tab=workflows'

const prettify = (s) =>
  (s || '').replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())

/** A step that has just been added from the palette.
 *
 *  The key is derived from the name ONCE, by StageEditor's own
 *  slugFromLabel, and then frozen — see the header of that file. A step
 *  key is what workflow_step_executions.step_key records and what A2
 *  drift detection matches a documented step against; renaming one in a
 *  later version silently stops that match without breaking anything
 *  visible. */
function newStep(type, existing) {
  const name = `New ${prettify(type).toLowerCase()}`
  return {
    key: slugFromLabel(name, existing.map((s) => s.key)),
    seq: existing.length + 1,
    name,
    type,
    actor: type === 'system' || type === 'notification' ? 'system' : 'employee',
    config: {},
    sla_hours: null,
    on_timeout: null,
  }
}

export default function WorkflowEngineBuilder() {
  const { key } = useParams()

  const [meta, setMeta] = useState(null)
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [busy, setBusy] = useState(null) // 'save' | 'publish' | 'review' | 'edit'

  // The working graph. Held apart from `data` so an unsaved edit is
  // visibly unsaved rather than silently indistinguishable from what the
  // server holds.
  const [steps, setSteps] = useState([])
  const [transitions, setTransitions] = useState([])
  const [versionCfg, setVersionCfg] = useState({})
  const [dirty, setDirty] = useState(false)

  const [selectedKey, setSelectedKey] = useState(null)
  const [findings, setFindings] = useState([])
  const [showPublish, setShowPublish] = useState(false)
  const [showFork, setShowFork] = useState(false)
  const [diff, setDiff] = useState(null)
  const [showDiff, setShowDiff] = useState(false)

  const adopt = useCallback((payload) => {
    setData(payload)
    const g = payload.graph || { steps: [], transitions: [] }
    setSteps(g.steps || [])
    setTransitions(g.transitions || [])
    setVersionCfg({
      automation_mode: g.automation_mode,
      trigger_type: g.trigger_type,
      trigger_config: g.trigger_config || {},
      modules: g.modules || [],
    })
    setFindings(payload.validation?.findings || [])
    setDirty(false)
  }, [])

  const load = useCallback(
    (versionId) => {
      setLoading(true)
      setError(null)
      api
        .getDefinitionByKey(key, versionId)
        .then(adopt)
        .catch(setError)
        .finally(() => setLoading(false))
    },
    [key, adopt]
  )

  useEffect(() => {
    api.getMeta().then(setMeta).catch(() => {})
  }, [])

  useEffect(() => {
    load()
  }, [load])

  // ── SLA suggestions (Batch G) ──────────────────────────────────────
  //
  // Fetched per version, alongside the graph, and held SEPARATELY from
  // `steps`. Merging them into step state would be the one mistake this
  // feature must not make: a derived number sitting in the same field as
  // an entered one is indistinguishable at save time, and the whole
  // point is that a suggestion is not a value until somebody says so.
  //
  // Failure is silent on purpose. Suggestions are an assist; the builder
  // works without them and an error banner for a missing convenience
  // would be noise on a screen that already carries publish blockers.
  const [slaSuggestions, setSlaSuggestions] = useState({})
  // shown_version.id — NOT .version_id, which _version_row does not emit.
  // The fallback is _summarise's version_id, i.e. the LATEST version, so
  // reading the wrong field here would silently fetch suggestions for the
  // latest version while the reviewer looks at an older one.
  const versionId = data?.shown_version?.id || data?.version_id

  useEffect(() => {
    if (!versionId) return
    let live = true
    api
      .fetchSlaSuggestions(versionId)
      .then((r) => {
        if (!live) return
        const byKey = {}
        for (const s of r.suggestions || []) byKey[s.step_key] = s
        setSlaSuggestions(byKey)
      })
      .catch(() => {
        if (live) setSlaSuggestions({})
      })
    return () => {
      live = false
    }
  }, [versionId])

  // ------------------------------------------------------------------
  // Is the ordered list a faithful picture of this graph?
  // ------------------------------------------------------------------
  const linear = useMemo(
    () => isLinearChain(steps, transitions),
    [steps, transitions]
  )
  const editable = !!data?.editable
  const topologyLocked = !editable || !linear

  const setStepsAndChain = (next) => {
    const seq = resequence(next)
    setSteps(seq)
    // Only the ordered list's own chain is rebuilt. A graph the list
    // cannot express is never reached here — topology editing is locked
    // when linear is false — so this can never flatten a branch.
    setTransitions(chainTransitions(seq))
    setDirty(true)
  }

  const patchStep = (stepKey, patch) => {
    setSteps((prev) =>
      prev.map((s) => (s.key === stepKey ? { ...s, ...patch } : s))
    )
    setDirty(true)
  }

  const selected = steps.find((s) => s.key === selectedKey) || null

  // ------------------------------------------------------------------
  // Writes
  // ------------------------------------------------------------------
  const payload = () => ({
    steps: steps.map((s, i) => ({
      key: s.key,
      seq: i + 1,
      name: s.name,
      type: s.type,
      actor: s.actor,
      config: s.config || {},
      sla_hours: s.sla_hours ?? null,
      on_timeout: s.on_timeout ?? null,
    })),
    transitions: transitions.map((t) => ({
      from: t.from ?? null,
      to: t.to,
      label: t.label ?? null,
      condition: t.condition ?? null,
      is_loop: !!t.is_loop,
    })),
    automation_mode: versionCfg.automation_mode,
    trigger_type: versionCfg.trigger_type,
    trigger_config: versionCfg.trigger_config,
    modules: versionCfg.modules,
  })

  const save = async () => {
    setBusy('save')
    setError(null)
    try {
      const res = await api.saveVersion(data.shown_version.id, payload())
      // R5 — a save NEVER fails on findings. It returns them, and the
      // author keeps working. Publish is where they are fatal.
      setFindings(res.validation.findings)
      setDirty(false)
    } catch (e) {
      setError(e)
    } finally {
      setBusy(null)
    }
  }

  const doPublish = async () => {
    setBusy('publish')
    setError(null)
    try {
      const res = await api.publishVersion(data.shown_version.id)
      adopt(res)
      setShowPublish(false)
    } catch (e) {
      // 422 carries the findings, each anchored to its step. They go on
      // the rows, not into an alert.
      if (e.findings) {
        setFindings(e.findings)
        setShowPublish(false)
      }
      setError(e)
    } finally {
      setBusy(null)
    }
  }

  const review = async (state) => {
    setBusy('review')
    setError(null)
    try {
      await api.setReviewState(data.shown_version.id, state)
      load(data.shown_version.id)
    } catch (e) {
      setError(e)
    } finally {
      setBusy(null)
    }
  }

  const openForEditing = async () => {
    setBusy('edit')
    setError(null)
    try {
      const res = await api.editPublished(data.id)
      adopt(res)
      setShowFork(false)
    } catch (e) {
      setError(e)
    } finally {
      setBusy(null)
    }
  }

  const loadDiff = async () => {
    setShowDiff((v) => !v)
    if (diff) return
    try {
      setDiff(await api.getDiff(data.shown_version.id))
    } catch (e) {
      setError(e)
    }
  }

  // ------------------------------------------------------------------
  if (loading) {
    return (
      <MessengerLayout>
        <div className="cases-root min-h-screen">
          <div className="px-5 py-6 sm:px-8">
            <Spinner label="Loading the definition" />
          </div>
        </div>
      </MessengerLayout>
    )
  }

  if (!data) {
    return (
      <MessengerLayout>
        <div className="cases-root min-h-screen">
          <div className="px-5 py-6 sm:px-8">
            <Refusal error={error} />
            <Link to={LIBRARY} className="btn mt-3">
              <ArrowLeft size={14} strokeWidth={1.9} /> Back to the workflow library
            </Link>
          </div>
        </div>
      </MessengerLayout>
    )
  }

  const owner = OWNER[data.execution_owner] || OWNER.none
  const OwnerIcon = owner.icon
  const version = data.shown_version
  const reviewed = version?.review_state === 'reviewed'
  const vFindings = versionFindings(findings)

  return (
    <MessengerLayout>
      <div className="cases-root min-h-screen">
        <div className="w-full px-5 py-6 sm:px-8">
          {/* ---------------- header ---------------- */}
          <div className="flex flex-wrap items-end justify-between gap-4">
            <div className="min-w-0">
              <Link
                to={LIBRARY}
                className="eyebrow inline-flex items-center gap-1 hover:text-accent"
              >
                <ArrowLeft size={12} strokeWidth={2} /> Admin Settings → Workflows
              </Link>
              <h1 className="mt-1 font-head text-[22px] font-bold leading-tight text-ink">
                {data.ref_id != null && (
                  <span className="mono mr-2 text-faint">#{data.ref_id}</span>
                )}
                {data.name}
              </h1>
              <p className="mono mt-1 text-[11px] text-faint">{data.key}</p>
            </div>

            <div className="flex flex-wrap items-center gap-2">
              <Chip tone={owner.tone} title={owner.help}>
                <OwnerIcon size={12} strokeWidth={1.9} /> {owner.label}
              </Chip>

              <select
                className="field w-auto"
                value={version?.id || ''}
                onChange={(e) => {
                  setDiff(null)
                  setShowDiff(false)
                  load(e.target.value)
                }}
                aria-label="Version"
              >
                {data.versions.map((v) => (
                  <option key={v.id} value={v.id}>
                    v{v.version} · {v.status}
                    {v.review_state === 'reviewed' ? ' · reviewed' : ''}
                  </option>
                ))}
              </select>

              <Chip tone={version?.status === 'published' ? 'accent' : undefined}>
                {version?.status}
              </Chip>

              {editable ? (
                <>
                  <Button
                    icon={Save}
                    busy={busy === 'save'}
                    disabled={!dirty}
                    onClick={save}
                  >
                    Save draft
                  </Button>
                  <Button
                    variant="primary"
                    icon={Upload}
                    disabled={dirty}
                    title={
                      dirty
                        ? 'Save the draft first — publish freezes what is stored, not what is on screen'
                        : undefined
                    }
                    onClick={() => setShowPublish(true)}
                  >
                    Publish
                  </Button>
                </>
              ) : (
                <Button
                  icon={Pencil}
                  busy={busy === 'edit'}
                  onClick={() =>
                    data.is_system ? setShowFork(true) : openForEditing()
                  }
                >
                  Edit
                </Button>
              )}

              <Button icon={GitCompare} onClick={loadDiff}>
                {showDiff ? 'Hide diff' : 'Diff'}
              </Button>
            </div>
          </div>

          {error && (
            <div className="mt-4">
              <Refusal error={error} onDismiss={() => setError(null)} />
            </div>
          )}

          {/* ---------------- A1, immediately under Publish ----------------
              Not in a panel a reader might not open. This is the claim
              that has to survive contact with a step list. */}
          <div className="mt-5 grid gap-4 lg:grid-cols-[1fr_360px]">
            <div className="panel px-5 py-4">
              <PublishEffects
                effects={data.validation?.publish_commits}
                prominent
              />
            </div>

            <div className="space-y-4">
              {data.held_from_publish && (
                <Notice tone="amber" icon={PauseCircle} title="Held from publish">
                  {data.held_from_publish} — this is a review instruction, not a
                  validation rule. Nothing here refuses the publish; somebody has
                  to decide.
                </Notice>
              )}

              {/* 072 — WHAT WE ALREADY KNOW THIS DEFINITION GETS WRONG.
                  The most useful thing on the screen for somebody about
                  to sign off a step list: which parts of it the code does
                  not actually do. Written by a human or by
                  wf_engine/corrections.py, never inferred. */}
              {/* 073 — AND WHICH KIND. A clarification and a "documented
                  but not built" rendered identically until now; the
                  reviewer who learns to skim the first skims the second.
                  Vocabulary shared with the library via
                  api.coverageKindOf so the two screens cannot describe
                  the same gap differently. */}
              {data.coverage_note && (() => {
                const ck = api.coverageKindOf(data.coverage_kind)
                return (
                  <Notice tone={ck.tone} title={ck.title}>
                    {ck.help && <p className="mb-1 opacity-80">{ck.help}</p>}
                    {data.coverage_note}
                  </Notice>
                )
              })()}

              {/* THE A3 REVIEW CONTROL. This is how the 39 get marked off. */}
              <div className="panel px-5 py-4">
                <div className="eyebrow mb-1">Review</div>
                {reviewed ? (
                  <>
                    <p className="flex items-center gap-1.5 text-[12.5px] text-accent">
                      <CheckCircle2 size={14} strokeWidth={1.9} /> Reviewed
                    </p>
                    <p className="mt-1 text-[11.5px] leading-relaxed text-faint">
                      {version.reviewed_by_email} on{' '}
                      {(version.reviewed_at || '').slice(0, 10)}. The mark is on
                      version {version.version} and freezes with it when it is
                      published — a later version starts unreviewed, because
                      nobody has read that content.
                    </p>
                  </>
                ) : (
                  <p className="text-[11.5px] leading-relaxed text-faint">
                    Not yet reviewed. Mark this once the SLA hours and timeouts
                    are right — that is A3&rsquo;s first priority and the part an
                    auditor will hold the organisation to.
                  </p>
                )}
                {editable && (
                  <div className="mt-2.5">
                    <Button
                      size="sm"
                      variant={reviewed ? 'ghost' : 'primary'}
                      icon={reviewed ? undefined : ShieldCheck}
                      busy={busy === 'review'}
                      onClick={() => review(reviewed ? 'pending' : 'reviewed')}
                    >
                      {reviewed ? 'Clear the review mark' : 'Mark as reviewed'}
                    </Button>
                  </div>
                )}
                {!editable && (
                  <p className="mt-2 text-[11px] text-faint">
                    This version is {version?.status}; its review record is
                    frozen with everything else it says (R1).
                  </p>
                )}
              </div>
            </div>
          </div>

          {showDiff && (
            <div className="mt-4">
              <DiffPanel diff={diff} />
            </div>
          )}

          {/* ---------------- the three columns ---------------- */}
          <div className="mt-4 grid gap-4 lg:grid-cols-[190px_1fr_360px]">
            {/* -------- palette -------- */}
            <Panel eyebrow="Add a step" className="self-start">
              {topologyLocked ? (
                <p className="text-[11.5px] leading-relaxed text-faint">
                  {!editable
                    ? 'A published version cannot be edited. Open it for editing to add steps.'
                    : 'This graph branches, so the ordered list is not the whole of it. Adding a step here would rewrite the edges from row order and delete the branch.'}
                </p>
              ) : (
                <div className="space-y-1.5">
                  {(meta?.step_types || []).map((t) => (
                    <Button
                      key={t}
                      size="sm"
                      icon={Plus}
                      className="w-full justify-start"
                      onClick={() =>
                        setStepsAndChain([...steps, newStep(t, steps)])
                      }
                    >
                      {prettify(t)}
                    </Button>
                  ))}
                </div>
              )}
            </Panel>

            {/* -------- the ordered step list -------- */}
            <Panel
              eyebrow="Steps, in order"
              title={`${steps.length} step${steps.length === 1 ? '' : 's'}`}
              right={
                dirty ? (
                  <Chip tone="amber">Unsaved</Chip>
                ) : (
                  <Chip>Saved</Chip>
                )
              }
            >
              {!linear && (
                <div className="mb-3">
                  <Notice tone="amber" title="This graph branches">
                    One or more steps have more than one outgoing transition, or
                    an edge carries a condition or a loop flag. An ordered list
                    cannot show that, so reordering, adding and removing are
                    locked here — rewriting the edges from row order would
                    delete the branch without ever having displayed it. Editing
                    a step&rsquo;s own fields is unaffected.
                  </Notice>
                </div>
              )}

              <OrderedList
                items={steps}
                onChange={setStepsAndChain}
                disabled={topologyLocked}
                labelOf={(s) => s.name}
                selectedKey={selectedKey}
                onSelect={(s) => setSelectedKey(s.key)}
                toneOf={(s) => (findingsFor(findings, s.key).length ? 'error' : null)}
                empty="This version has no steps. A workflow with no steps fails V1 and cannot be published."
                renderRow={(s) => (
                  <StepRow
                    step={s}
                    findings={findingsFor(findings, s.key)}
                    isEntry={steps[0]?.key === s.key}
                  />
                )}
              />
            </Panel>

            {/* -------- inspector -------- */}
            <div className="space-y-4">
              <Inspector
                step={selected}
                meta={meta}
                findings={selected ? findingsFor(findings, selected.key) : []}
                disabled={!editable}
                onChange={(patch) => patchStep(selected.key, patch)}
                suggestion={selected ? slaSuggestions[selected.key] : null}
              />

              <VersionPanel
                cfg={versionCfg}
                meta={meta}
                findings={vFindings}
                disabled={!editable}
                onChange={(patch) => {
                  setVersionCfg((c) => ({ ...c, ...patch }))
                  setDirty(true)
                }}
              />

              <div className="panel px-5 py-4">
                <BlockingFindings
                  findings={findings}
                  onJumpToStep={(k) => setSelectedKey(k)}
                />
                {findings.length === 0 && (
                  <p className="text-[12px] text-faint">
                    Nothing blocks publishing this version.
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ---------------- publish confirmation ---------------- */}
      <Modal
        open={showPublish}
        onClose={() => setShowPublish(false)}
        title={`Publish version ${version?.version}?`}
        subtitle={data.name}
      >
        <div className="space-y-3">
          {data.held_from_publish && (
            <Notice tone="amber" icon={PauseCircle} title="This one is held">
              {data.held_from_publish} Publishing it anyway is a decision
              somebody has to own — the validator will not stop you.
            </Notice>
          )}
          <PublishEffects effects={data.validation?.publish_commits} prominent />
          <p className="text-[12px] leading-relaxed text-faint">
            Publishing freezes this version. It cannot be edited afterwards —
            changing it later means creating a new version (R1), because #35,
            #41 and #42 are legal evidence and the rule cannot be selective.
          </p>
          <div className="flex justify-end gap-2">
            <Button onClick={() => setShowPublish(false)}>Cancel</Button>
            <Button
              variant="primary"
              icon={Upload}
              busy={busy === 'publish'}
              onClick={doPublish}
            >
              Publish
            </Button>
          </div>
        </div>
      </Modal>

      {/* ---------------- fork warning ---------------- */}
      <Modal
        open={showFork}
        onClose={() => setShowFork(false)}
        title="Editing this makes your own copy"
        subtitle={data.name}
      >
        <div className="space-y-3">
          <Notice tone="amber" title="Your organisation stops tracking the original">
            This is a system definition, shared by every organisation. Editing it
            creates a copy owned by yours, under the same key, and from then on
            your copy is what this organisation sees. Corrections made to the
            shared definition — a wrong summary, a corrected step name — will no
            longer reach you.
          </Notice>
          <p className="text-[12px] leading-relaxed text-faint">
            The copy records which version it came from, so the difference stays
            answerable rather than invisible.
          </p>
          <div className="flex justify-end gap-2">
            <Button onClick={() => setShowFork(false)}>Cancel</Button>
            <Button
              variant="primary"
              icon={Pencil}
              busy={busy === 'edit'}
              onClick={openForEditing}
            >
              Make a copy and edit it
            </Button>
          </div>
        </div>
      </Modal>
    </MessengerLayout>
  )
}

// ======================================================================
// The row body
// ======================================================================

function StepRow({ step, findings, isEntry }) {
  return (
    <>
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-[13px] text-ink">{step.name}</span>
        <Chip>{prettify(step.type)}</Chip>
        <Chip>{prettify(step.actor)}</Chip>
        {isEntry && <Chip tone="blue">Entry</Chip>}
        {step.sla_hours != null && (
          <Chip tone="accent">SLA {step.sla_hours}h</Chip>
        )}
        {step.sla_hours != null && !step.on_timeout && (
          <Chip tone="amber" title="V14 — an SLA with no timeout means nothing happens when it is breached">
            no timeout
          </Chip>
        )}
      </div>

      <p className="mono text-[11px] text-faint">{step.key}</p>

      {/* THE FINDINGS, ON THE ROW THEY ARE ABOUT. Not a toast: a message
          that fades has to be memorised before it can be acted on. */}
      {findings.map((f, i) => (
        <p
          key={`${f.rule}-${i}`}
          className="flex items-start gap-1.5 rounded border border-danger-line bg-danger-dim px-2 py-1.5 text-[11.5px] leading-relaxed text-ink"
        >
          <AlertTriangle size={12} strokeWidth={2} className="mt-0.5 shrink-0 text-danger" />
          <span>
            <span className="mono mr-1 text-[10px] text-danger">{f.rule}</span>
            {f.message}
          </span>
        </p>
      ))}
    </>
  )
}

// ======================================================================
// The inspector
// ======================================================================

function Inspector({ step, meta, findings, disabled, onChange, suggestion }) {
  if (!step) {
    return (
      <Panel eyebrow="Inspector">
        <p className="text-[12px] text-faint">
          Select a step to edit its name, type, actor, SLA and configuration.
        </p>
      </Panel>
    )
  }

  const cfg = step.config || {}
  const setCfg = (patch) => onChange({ config: { ...cfg, ...patch } })

  // Keys the structured fields below do not own. SHOWN RATHER THAN
  // SILENTLY CARRIED: they are preserved on save either way, and a value
  // the system stores but never displays is how a step ends up with
  // configuration nobody knows about.
  const OWNED = ['approver_strategy', 'approver_user_id', 'notification_type', 'handler']
  const extra = Object.keys(cfg).filter((k) => !OWNED.includes(k))

  return (
    <Panel eyebrow="Inspector" title={step.name}>
      <div className="space-y-3">
        {findings.map((f, i) => (
          <div
            key={i}
            className="rounded-md border border-danger-line bg-danger-dim px-3 py-2 text-[11.5px] leading-relaxed text-ink"
          >
            <span className="mono mr-1 text-[10px] text-danger">{f.rule}</span>
            {f.message}
          </div>
        ))}

        <Field label="Name">
          <input
            className="field"
            maxLength={200}
            value={step.name}
            disabled={disabled}
            onChange={(e) => onChange({ name: e.target.value })}
          />
        </Field>

        {/* THE KEY, SHOWN AND FROZEN — the same rule as StageEditor's
            stage key, for a different table. */}
        <p className="text-[11px] leading-relaxed text-faint">
          Stored as <span className="mono text-muted">{step.key}</span> — fixed
          once the step exists. Step executions and running instances record
          which step they are on by this name, and drift detection matches the
          documented step against it, so renaming it would quietly stop that
          match rather than break anything visible.
        </p>

        <div className="grid grid-cols-2 gap-3">
          <Field label="Type">
            <select
              className="field"
              value={step.type}
              disabled={disabled}
              onChange={(e) => onChange({ type: e.target.value })}
            >
              {(meta?.step_types || [step.type]).map((t) => (
                <option key={t} value={t}>
                  {prettify(t)}
                </option>
              ))}
            </select>
          </Field>

          <Field label="Actor">
            <select
              className="field"
              value={step.actor}
              disabled={disabled}
              onChange={(e) => onChange({ actor: e.target.value })}
            >
              {(meta?.step_actors || [step.actor]).map((a) => (
                <option key={a} value={a}>
                  {prettify(a)}
                </option>
              ))}
            </select>
          </Field>
        </div>

        {/* -------- SLA and timeout: A3's first priority -------- */}
        <div className="grid grid-cols-2 gap-3">
          <Field
            label="SLA hours"
            hint="Blank means no clock on this step."
          >
            <input
              className="field"
              type="number"
              min={1}
              max={100000}
              value={step.sla_hours ?? ''}
              disabled={disabled}
              onChange={(e) =>
                onChange({
                  sla_hours:
                    e.target.value === '' ? null : Number(e.target.value),
                })
              }
            />
          </Field>

          <Field label="On timeout">
            <select
              className="field"
              value={(step.on_timeout || {}).action || ''}
              disabled={disabled}
              onChange={(e) =>
                onChange({
                  on_timeout: e.target.value
                    ? { ...(step.on_timeout || {}), action: e.target.value }
                    : null,
                })
              }
            >
              <option value="">Nothing</option>
              {(meta?.on_timeout_actions || []).map((a) => (
                <option key={a.value} value={a.value} title={a.help}>
                  {a.label}
                </option>
              ))}
            </select>
          </Field>
        </div>

        {step.sla_hours != null && !step.on_timeout && (
          <p className="rounded border border-amber-line bg-amber-dim px-2.5 py-1.5 text-[11.5px] leading-relaxed text-ink">
            <span className="mono mr-1 text-[10px] text-amber">V14</span>
            An SLA with no timeout means nothing happens when it is breached.
            This warns while you are editing and blocks the publish.
          </p>
        )}

        <SlaSuggestion
          suggestion={suggestion}
          entered={step.sla_hours}
          disabled={disabled}
          onAccept={() =>
            onChange({
              sla_hours: suggestion.hours,
              on_timeout: step.on_timeout || suggestion.on_timeout,
            })
          }
        />

        {/* -------- config: the optional A3 enrichment -------- */}
        {step.type === 'approval' && (
          <ApproverStrategy cfg={cfg} meta={meta} disabled={disabled} setCfg={setCfg} />
        )}

        {step.type === 'notification' && (
          <Field
            label="Notification type"
            hint="Optional for a documented workflow — V9 only requires it where the engine is the thing sending. Recording it is what makes the definition say which message this step is."
          >
            <select
              className="field"
              value={cfg.notification_type || ''}
              disabled={disabled}
              onChange={(e) =>
                setCfg({ notification_type: e.target.value || undefined })
              }
            >
              <option value="">Not recorded</option>
              {(meta?.notification_types || []).map((t) => (
                <option key={t} value={t}>
                  {t}
                </option>
              ))}
            </select>
          </Field>
        )}

        {step.type === 'system' && (
          <Field
            label="Handler"
            hint="The engine's step-handler registry is empty until Phase 6, whose scope is engine-owned definitions only. For a documented workflow this records which function does the work."
          >
            <input
              className="field"
              value={cfg.handler || ''}
              disabled={disabled}
              placeholder="not recorded"
              onChange={(e) => setCfg({ handler: e.target.value || undefined })}
            />
          </Field>
        )}

        {extra.length > 0 && (
          <div>
            <div className="eyebrow mb-1">Other configuration</div>
            <pre className="mono overflow-x-auto rounded border border-line bg-surface-2 px-2.5 py-2 text-[11px] text-muted">
              {JSON.stringify(
                Object.fromEntries(extra.map((k) => [k, cfg[k]])),
                null,
                2
              )}
            </pre>
            <p className="mt-1 text-[11px] text-faint">
              Kept exactly as it is when you save. Shown because a value the
              system stores but never displays is worse than one shown as fixed.
            </p>
          </div>
        )}
      </div>
    </Panel>
  )
}

/** D11's three strategies. `role:` is a PREFIX, not a value — the role
 *  itself follows it — which is why this is a component and not one
 *  more <select>. */
/* ── SLA suggestion (Batch G) ──────────────────────────────────────────
 *
 * A SUGGESTION MUST NOT LOOK LIKE A VALUE. The whole risk of this
 * feature is that a derived number gets mistaken for a decision
 * somebody made: it goes into a published definition, the escalation
 * engine acts on it hourly, and eighteen months later it is quoted back
 * as company policy. So this never writes into the input, never
 * pre-fills, and is drawn OUTSIDE the field in its own dashed box with
 * its own wording. The field stays empty until a person clicks.
 *
 * IT SHOWS ITS WORKING. `provenance` names the table row or the
 * file:line the number came from, so a reviewer can check the claim
 * rather than trust the box.
 *
 * AND IT SHOWS ITS REFUSALS. When the backend found no basis it says so,
 * with what it checked. That is deliberately as visible as a suggestion:
 * 293 of 330 steps have no derivable SLA, and a screen that renders only
 * the 37 would imply the other 293 were simply not looked at. */
function SlaSuggestion({ suggestion, entered, disabled, onAccept }) {
  if (!suggestion) return null

  // Never argue with a value a human entered. The backend already
  // refuses to re-suggest over one; this is the second half of that, so
  // an accidental click cannot overwrite a decision.
  if (entered != null) return null

  if (!suggestion.found) {
    return (
      <p className="rounded border border-dashed border-line px-2.5 py-1.5 text-[11.5px] leading-relaxed text-muted">
        <span className="mono mr-1 text-[10px]">NO BASIS</span>
        {suggestion.why_not}
      </p>
    )
  }

  return (
    <div className="rounded border border-dashed border-accent-line bg-accent-dim/40 px-2.5 py-2">
      <div className="flex items-center justify-between gap-3">
        <span className="text-[11.5px] text-ink">
          <span className="mono mr-1 text-[10px] text-accent">SUGGESTED</span>
          {suggestion.hours}h
          {suggestion.on_timeout ? (
            <span className="text-muted">
              {' '}· then {suggestion.on_timeout.action}
            </span>
          ) : null}
        </span>
        <button
          type="button"
          disabled={disabled}
          onClick={onAccept}
          className="shrink-0 rounded border border-accent-line px-2 py-0.5 text-[11px] text-accent hover:bg-accent-dim disabled:opacity-40"
        >
          Use this
        </button>
      </div>
      {/* The provenance is the point. Small, but never hidden behind a
          tooltip — a claim you have to hover to read is a claim most
          people will not check. */}
      <p className="mt-1 text-[10.5px] leading-relaxed text-muted">
        {suggestion.provenance}
      </p>
    </div>
  )
}


function ApproverStrategy({ cfg, meta, disabled, setCfg }) {
  const raw = cfg.approver_strategy || ''
  const kind = raw.startsWith('role:') ? 'role:' : raw
  const role = raw.startsWith('role:') ? raw.slice(5) : ''

  return (
    <div className="space-y-3">
      <Field
        label="Approver strategy"
        hint="Optional for a documented workflow — V7 only requires it where the engine is the thing resolving an approver. Recording it is the A3 enrichment pass."
      >
        <select
          className="field"
          value={kind}
          disabled={disabled}
          onChange={(e) => {
            const v = e.target.value
            setCfg({
              approver_strategy:
                v === '' ? undefined : v === 'role:' ? 'role:' : v,
              approver_user_id:
                v === 'named_user' ? cfg.approver_user_id : undefined,
            })
          }}
        >
          <option value="">Not recorded</option>
          {(meta?.approver_strategies || []).map((s) => (
            <option key={s.value} value={s.value}>
              {s.label}
            </option>
          ))}
        </select>
      </Field>

      {kind === 'role:' && (
        <Field label="Which role">
          <select
            className="field"
            value={role}
            disabled={disabled}
            onChange={(e) => setCfg({ approver_strategy: `role:${e.target.value}` })}
          >
            <option value="">Choose a role</option>
            {(meta?.approver_roles || []).map((r) => (
              <option key={r} value={r}>
                {r}
              </option>
            ))}
          </select>
        </Field>
      )}

      {kind === 'named_user' && (
        <Field
          label="Approver user id"
          hint="A specific person. V7 refuses 'named_user' with nothing named."
        >
          <input
            className="field"
            value={cfg.approver_user_id || ''}
            disabled={disabled}
            onChange={(e) =>
              setCfg({ approver_user_id: e.target.value || undefined })
            }
          />
        </Field>
      )}
    </div>
  )
}

// ======================================================================
// The version's own fields — where the findings with no step live
// ======================================================================

function VersionPanel({ cfg, meta, findings, disabled, onChange }) {
  const tc = cfg.trigger_config || {}
  const setTc = (patch) => onChange({ trigger_config: { ...tc, ...patch } })

  return (
    <Panel eyebrow="Trigger and mode">
      <div className="space-y-3">
        {findings.map((f, i) => (
          <div
            key={i}
            className="rounded-md border border-danger-line bg-danger-dim px-3 py-2 text-[11.5px] leading-relaxed text-ink"
          >
            <span className="mono mr-1 text-[10px] text-danger">{f.rule}</span>
            {f.message}
          </div>
        ))}

        <div className="grid grid-cols-2 gap-3">
          <Field label="Automation">
            <select
              className="field"
              value={cfg.automation_mode || ''}
              disabled={disabled}
              onChange={(e) => onChange({ automation_mode: e.target.value })}
            >
              {(meta?.automation_modes || []).map((m) => (
                <option key={m} value={m}>
                  {prettify(m)}
                </option>
              ))}
            </select>
          </Field>

          <Field label="Trigger">
            <select
              className="field"
              value={cfg.trigger_type || ''}
              disabled={disabled}
              onChange={(e) => onChange({ trigger_type: e.target.value })}
            >
              {(meta?.trigger_types || []).map((t) => (
                <option key={t} value={t}>
                  {prettify(t)}
                </option>
              ))}
            </select>
          </Field>
        </div>

        {cfg.trigger_type === 'schedule' && (
          <>
            <Field
              label="Cron"
              hint="The 12 seeded schedule triggers ship with no cron on purpose — the real expression is in main.py and has to be read off it. Inventing payroll or tax-year timing is not this screen's job either."
            >
              <input
                className="field mono"
                value={tc.cron || ''}
                disabled={disabled}
                placeholder="0 6 * * 1-5"
                onChange={(e) => setTc({ cron: e.target.value || undefined })}
              />
            </Field>
            <Field
              label="Timezone"
              hint="A cron expression without one runs at a different moment for every tenant."
            >
              <input
                className="field"
                value={tc.timezone || ''}
                disabled={disabled}
                placeholder="Europe/London"
                onChange={(e) => setTc({ timezone: e.target.value || undefined })}
              />
            </Field>
          </>
        )}

        {cfg.trigger_type === 'event' && (
          <Field
            label="Event"
            hint="Only registered events can arm a workflow. The four placeholder names in the seed are descriptions of the fact that an event happens, not event names, and V11 refusing them is the validator working."
          >
            <select
              className="field"
              value={tc.event || ''}
              disabled={disabled}
              onChange={(e) => setTc({ event: e.target.value || undefined })}
            >
              <option value="">
                {tc.event ? `${tc.event} (not registered)` : 'Not recorded'}
              </option>
              {(meta?.workflow_events || []).map((e2) => (
                <option key={e2} value={e2}>
                  {e2}
                </option>
              ))}
            </select>
          </Field>
        )}
      </div>
    </Panel>
  )
}

// ======================================================================
// The diff
// ======================================================================

function DiffPanel({ diff }) {
  if (!diff) {
    return (
      <Panel eyebrow="Diff">
        <Spinner label="Comparing" />
      </Panel>
    )
  }
  const d = diff.diff
  if (!d.has_previous) {
    return (
      <Panel eyebrow="Diff">
        <p className="text-[12px] text-faint">
          This is version {diff.version.version} and there is no earlier one to
          compare it with. Every seeded definition is at version 1 — the
          catalogue was imported once and nothing has been published.
        </p>
      </Panel>
    )
  }

  const nothing =
    d.steps_added.length === 0 &&
    d.steps_removed.length === 0 &&
    d.steps_changed.length === 0 &&
    d.transitions_added.length === 0 &&
    d.transitions_removed.length === 0 &&
    d.fields_changed.length === 0

  return (
    <Panel
      eyebrow="Diff"
      title={`v${diff.version.version} against v${diff.against.version}`}
    >
      {nothing ? (
        <p className="text-[12px] text-faint">
          Identical. Nothing in the graph, the steps or the trigger differs.
        </p>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2">
          <DiffList
            title="Steps added"
            rows={d.steps_added.map((s) => `${s.name} (${s.key})`)}
            tone="accent"
          />
          <DiffList
            title="Steps removed"
            rows={d.steps_removed.map((s) => `${s.name} (${s.key})`)}
            tone="danger"
          />
          <DiffList
            title="Transitions added"
            rows={d.transitions_added.map((t) => `${t.from || 'ENTRY'} → ${t.to}`)}
            tone="accent"
          />
          <DiffList
            title="Transitions removed"
            rows={d.transitions_removed.map((t) => `${t.from || 'ENTRY'} → ${t.to}`)}
            tone="danger"
          />

          {d.steps_changed.length > 0 && (
            <div className="sm:col-span-2">
              <div className="eyebrow mb-1">Steps changed</div>
              <ul className="space-y-2">
                {d.steps_changed.map((s) => (
                  <li
                    key={s.key}
                    className="rounded-md border border-line bg-surface-2 px-3 py-2"
                  >
                    <p className="text-[12.5px] text-ink">{s.name}</p>
                    <ul className="mt-1 space-y-0.5">
                      {s.fields.map((f) => (
                        <li key={f.field} className="text-[11.5px] text-faint">
                          <span className="text-muted">{prettify(f.field)}</span>{' '}
                          <span className="mono text-danger">
                            {JSON.stringify(f.from)}
                          </span>{' '}
                          →{' '}
                          <span className="mono text-accent">
                            {JSON.stringify(f.to)}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {d.fields_changed.length > 0 && (
            <div className="sm:col-span-2">
              <div className="eyebrow mb-1">Version fields</div>
              <ul className="space-y-0.5">
                {d.fields_changed.map((f) => (
                  <li key={f.field} className="text-[11.5px] text-faint">
                    <span className="text-muted">{prettify(f.field)}</span>{' '}
                    <span className="mono text-danger">{JSON.stringify(f.from)}</span>{' '}
                    → <span className="mono text-accent">{JSON.stringify(f.to)}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </Panel>
  )
}

function DiffList({ title, rows, tone }) {
  if (!rows || rows.length === 0) return null
  return (
    <div>
      <div className="eyebrow mb-1">{title}</div>
      <ul className="space-y-0.5">
        {rows.map((r) => (
          <li
            key={r}
            className={`mono text-[11.5px] ${
              tone === 'danger' ? 'text-danger' : 'text-accent'
            }`}
          >
            {r}
          </li>
        ))}
      </ul>
    </div>
  )
}
